import classNames from 'classnames';
import { Field, Formik } from 'formik';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCheckoutState } from '../../../utils/checkoutState';
import { config, getAssetUrl } from '../../../utils/config';
import getValidator from '../../../utils/getValidator';
import { reportErrorToSentry } from '../../../utils/initializeSentry';
import { pushErrorTrackingEvent } from '../../../utils/tracking';
import SubHeadline from '../../atoms/SubHeadline';
import InputWithIconAndButtonField from '../../formik/InputWithIconAndButtonField';
import { showDefaultError, showNotification } from '../Notifications';
import './style.scss';

const LoyaltyProgram = ({ isDisabled }: { isDisabled: boolean }) => {
  const { formatMessage } = useIntl();
  const { checkoutState, setLoyaltyProgram, removeLoyaltyProgram } = useCheckoutState();
  const activeMemberships = checkoutState.orderState.memberships?.activated;
  const storedLoyaltyProgramCode = activeMemberships?.[0]?.membershipAccountNumber;
  const earnings =
    activeMemberships?.[0]?.earnings.points ?? checkoutState.orderState.memberships?.suggestions[0]?.earnings.points;

  const [isLoading, setLoading] = useState(false);

  const initialValues = {
    loyaltyProgramCode: storedLoyaltyProgramCode ?? '',
  };

  const statusCodeI18nKeys = {
    404: 'payment.loyaltyProgram.notifications.customerNotFound',
    409: 'payment.loyaltyProgram.notifications.alreadyAttached',
    422: 'payment.loyaltyProgram.notifications.customerNotFound',
  };

  const statusCodeTrackingEventAction = {
    404: 'general form',
    409: 'input errors',
    422: 'general form',
  };

  const addCustomerNumber = async ({ loyaltyProgramCode }: typeof initialValues) => {
    if (loyaltyProgramCode === '') {
      return;
    }
    setLoading(true);
    try {
      await setLoyaltyProgram({ cardKey: loyaltyProgramCode, type: config.features.loyaltyProgram.type });
    } catch (e: any) {
      const error = statusCodeI18nKeys[e.statusCode];
      if (error) {
        const message = formatMessage({ id: error });
        pushErrorTrackingEvent(statusCodeTrackingEventAction[e.statusCode], 'error', message);

        showNotification({
          level: 'error',
          text: message,
          autoClose: true,
          closable: true,
          singleInstance: true,
          reference: 'loyalty-program',
        });
      } else {
        console.error(e);
        reportErrorToSentry(e);
        showDefaultError();
      }
    }
    setLoading(false);
  };

  const removeCustomerNumber = async ({ loyaltyProgramCode }: typeof initialValues) => {
    setLoading(true);
    try {
      await removeLoyaltyProgram({ cardKey: loyaltyProgramCode, type: config.features.loyaltyProgram.type });
    } catch (e) {
      console.error(e);
      reportErrorToSentry(e);
      showDefaultError();
    }
    setLoading(false);
  };

  const icon = config.features.loyaltyProgram.icon.enabled
    ? {
        src: !storedLoyaltyProgramCode
          ? getAssetUrl('inputs/loyaltyProgramActive.svg')
          : getAssetUrl('inputs/loyaltyProgram.svg'),
      }
    : undefined;

  const button = {
    text: storedLoyaltyProgramCode ? 'generic.remove' : 'generic.add',
    dataTestId: storedLoyaltyProgramCode ? 'payment-remove-payback' : 'payment-add-payback',
    disabled: isDisabled,
  };

  const submit = storedLoyaltyProgramCode ? removeCustomerNumber : addCustomerNumber;

  return (
    <div className="loyalty-program">
      <SubHeadline>
        <FormattedMessage id={`payment.loyaltyProgram.${storedLoyaltyProgramCode ? 'name' : 'headline'}`} />
      </SubHeadline>
      <Formik onSubmit={submit} initialValues={initialValues}>
        {({ submitForm }) => (
          <Field
            component={InputWithIconAndButtonField}
            icon={icon}
            button={{
              ...button,
              onClick: submitForm,
            }}
            placeholder={formatMessage({ id: 'payment.loyaltyProgram.label' })}
            className={classNames('loyalty-program', {
              'loyalty-program--disabled': storedLoyaltyProgramCode,
            })}
            name="loyaltyProgramCode"
            disabled={storedLoyaltyProgramCode}
            isLoading={isLoading}
            validate={getValidator(config.features.loyaltyProgram)}
            maxLength={config.features.loyaltyProgram.maxLength}
            isInputStatusIconDisabled
          />
        )}
      </Formik>
      <p className="loyalty-program__legend">
        <FormattedMessage id="payment.loyaltyProgram.legend" values={{ earnings }} />
      </p>
    </div>
  );
};

export default LoyaltyProgram;
